/* Eagle Leather: shipping dates. Reads window.elShipping (snippets/el-shipping-config.liquid) and
   1. rewrites every [data-el-ship-card-eta] element (shipping estimate only, e.g. "Ships tomorrow"; see EL 2026-10-12 (annotated 2026-10-15; change was published without notes, editor not recorded)), including cards added
      later by filters/pagination/quick view (ported from live Xtra assets/el-custom.js, IIFE 2);
   2. exposes the date math for the product page (assets/el-product.js) as window.elShipMath(cfg), so the ETA line,
      the countdown and the ZIP estimator all use ONE rule set:
        - ship (handover) date: today when today is a carrier day and it is before the cutoff; otherwise the next carrier
          day at or after the pack date (and at least handlingDays carrier days out). Closed dates affect only this date.
        - carrier transit: plain business days Mon-Fri counted from the ship date (store holidays don't stop UPS).
        - air services never arrive later than Ground (air days = min(air, ground)).
   3. owns the ZIP -> carrier ground transit table (3-digit ZIP prefix, ported unchanged from live Xtra
      el-ship-estimator) as window.elZipGroundDays(zip), shared by the product-page estimator (assets/el-product.js) and
      the cart estimator (assets/el-cart.js).
   Exposes window.elShippingEta() for other EL scripts. Loaded from snippets/el-head.liquid.
   EL 2026-10-01: date math moved into elShipMath; also loadable in Node (module.exports) for the test harness.
   EL 2026-10-02 (store info): ship weekdays come ONLY from cfg.shipDays - a list of JS weekday numbers (0 = Sunday …
   6 = Saturday) or day names ("Monday", "Sat"); window.elShipping.shipDays is filled from the Store info metaobject
   "Ship days". An empty/missing list means Monday-Friday. Carrier transit still counts Mon-Fri (carrier days).
   EL 2026-10-02 (pack vs carrier days): the store packs on packDays (default all 7) except storeClosedDates, during
   cfg.hours; carriers pick up on shipDays minus closedDates. See create() for the handover rule and the wording.
   EL 2026-10-02: ZIP table moved here from assets/el-product.js (was a "test hook" el-cart.js depended on); the DOM part
   runs once even if the file is loaded twice (el-cart.js can lazy-load it as a fallback). */
(function (root) {
  'use strict';

  var DAY_INDEX = { sun: 0, mon: 1, tue: 2, wed: 3, thu: 4, fri: 5, sat: 6 };
  // [1,2,3,4,5] | ['Monday', … ] | 'Mon,Tue' -> unique JS weekday numbers; `def` when nothing valid is given.
  function normalizeDays(list, def) {
    if (typeof list === 'string') list = list.split(',');
    var out = [];
    (Array.isArray(list) ? list : []).forEach(function (d) {
      var n = typeof d === 'number' ? d : (/^\s*\d\s*$/.test(String(d)) ? parseInt(d, 10) : DAY_INDEX[String(d).trim().slice(0, 3).toLowerCase()]);
      if (n === 7) n = 0;
      if (typeof n === 'number' && n >= 0 && n <= 6 && out.indexOf(n) === -1) out.push(n);
    });
    return out.length ? out : def.slice();
  }
  function normalizeShipDays(list) { return normalizeDays(list, [1, 2, 3, 4, 5]); }
  // Store hours, Monday first ("10:00-18:00" or "closed"); same defaults as snippets/el-store-info.liquid.
  var DEFAULT_HOURS = ['10:00-18:00', '10:00-18:00', '10:00-18:00', '10:00-18:00', '10:00-18:00', '10:00-18:00', '10:00-17:00'];
  function toMin(t) { var x = String(t).split(':'); return (+x[0]) * 60 + (+x[1] || 0); }
  function timeLabel(min) {
    var h = Math.floor(min / 60), m = min % 60, ap = h >= 12 ? 'PM' : 'AM', h12 = h % 12 || 12;
    return h12 + (m ? ':' + String(m).padStart(2, '0') : '') + ' ' + ap;
  }

  /* EL 2026-10-02 (pack vs carrier days). Three calendars:
       packDays + storeClosedDates + hours : the store is open and packs orders (Store info "Pack days", "Store closed dates")
       shipDays + closedDates              : a carrier picks up (Store info "Ship days", "No carrier pickup dates")
     A store-closed date is never a carrier day either (nobody is there to hand the parcels over).
     Handover (ship) date: today when today is a carrier day and it is before the cutoff; otherwise the later of
       - the handlingDays-th carrier day after today, and
       - the first carrier day on or after the pack date (pack date = today while the store is still open today,
         else the next open day). */
  function create(cfg) {
    var handling = Math.max(1, parseInt(cfg.handlingDays, 10) || 1);
    var closed = cfg.closedDates || [];
    var storeClosed = cfg.storeClosedDates || [];
    var shipDays = normalizeShipDays(cfg.shipDays);
    var packDays = normalizeDays(cfg.packDays, [0, 1, 2, 3, 4, 5, 6]);
    var hours = (cfg.hours && cfg.hours.length === 7) ? cfg.hours : DEFAULT_HOURS;

    function partsInTz(date) {
      var f = new Intl.DateTimeFormat('en-US', { timeZone: cfg.tz || 'America/Los_Angeles', year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false });
      var o = {};
      f.formatToParts(date).forEach(function (p) { o[p.type] = p.value; });
      return { y: +o.year, m: +o.month, d: +o.day, h: +o.hour % 24, min: +o.minute, s: +o.second };
    }
    function ymd(dt) { return dt.getUTCFullYear() + '-' + String(dt.getUTCMonth() + 1).padStart(2, '0') + '-' + String(dt.getUTCDate()).padStart(2, '0'); }
    function addDays(dt, n) { var d = new Date(dt.getTime()); d.setUTCDate(d.getUTCDate() + n); return d; }
    function isStoreClosedDate(dt) { return storeClosed.indexOf(ymd(dt)) > -1; }
    function hoursFor(dt) {
      var h = String(hours[(dt.getUTCDay() + 6) % 7] || 'closed').replace(/\s/g, '').toLowerCase();
      var m = h.match(/^(\d{1,2}:\d{2})-(\d{1,2}:\d{2})$/);
      return m ? { open: toMin(m[1]), close: toMin(m[2]) } : null;
    }
    // Store open (packing, in-store pickup) that day.
    function isOpenDay(dt) { return packDays.indexOf(dt.getUTCDay()) > -1 && !isStoreClosedDate(dt) && !!hoursFor(dt); }
    // A carrier picks up that day.
    function isShipDay(dt) { return shipDays.indexOf(dt.getUTCDay()) > -1 && closed.indexOf(ymd(dt)) === -1 && !isStoreClosedDate(dt); }
    function nextShipDay(dt, n) {
      var d = new Date(dt.getTime()), guard = 0;
      while (n > 0 && guard++ < 400) { d = addDays(d, 1); if (isShipDay(d)) n--; }
      return d;
    }
    function rollToShipDay(dt) {
      var d = new Date(dt.getTime()), guard = 0;
      while (!isShipDay(d) && guard++ < 400) d = addDays(d, 1);
      return d;
    }
    function nextOpenDay(dt) {
      var d = new Date(dt.getTime()), guard = 0;
      do { d = addDays(d, 1); } while (!isOpenDay(d) && guard++ < 400);
      return d;
    }
    function addBusinessDays(dt, n) {
      var d = new Date(dt.getTime());
      while (n > 0) { d = addDays(d, 1); var w = d.getUTCDay(); if (w !== 0 && w !== 6) n--; }
      return d;
    }
    function beforeCutoff(p) { return p.h < cfg.cutoffHour || (p.h === cfg.cutoffHour && p.min < cfg.cutoffMinute); }

    // Store-local "today" plus whether an order placed now ships today / is packed today.
    function now(at) {
      var p = partsInTz(at || new Date());
      var today = new Date(Date.UTC(p.y, p.m - 1, p.d));
      var mins = p.h * 60 + p.min;
      var hrs = isOpenDay(today) ? hoursFor(today) : null;
      var packDate = hrs && mins < hrs.close ? today : nextOpenDay(today);
      return { p: p, today: today, mins: mins, hours: hrs, storeClosed: !isOpenDay(today), packDate: packDate,
        shipsToday: isShipDay(today) && beforeCutoff(p) };
    }
    function shipFrom(t) {
      if (t.shipsToday) return t.today;
      var a = nextShipDay(t.today, handling), b = rollToShipDay(t.packDate);
      return a.getTime() >= b.getTime() ? a : b;
    }
    function shipDate(at) { return shipFrom(now(at)); }
    function packDate(at) { return now(at).packDate; }
    // Minutes left before today's cutoff, or -1 when an order placed now won't ship today.
    function minutesToCutoff(at) {
      var t = now(at);
      if (!t.shipsToday) return -1;
      return (cfg.cutoffHour * 60 + cfg.cutoffMinute) - t.mins;
    }
    function cutoffLabel() {
      var h = cfg.cutoffHour, ap = h >= 12 ? 'PM' : 'AM', h12 = h % 12 || 12;
      return h12 + ':' + String(cfg.cutoffMinute).padStart(2, '0') + ' ' + ap + ' ' + cfg.tzLabel;
    }
    // "today" / "tomorrow" / "Monday" (within a week) / "Monday, Dec 28"
    function dayWord(d, today) {
      var diff = Math.round((d.getTime() - today.getTime()) / 864e5);
      if (diff === 0) return 'today';
      if (diff === 1) return 'tomorrow';
      var opts = diff < 7 ? { weekday: 'long', timeZone: 'UTC' } : { weekday: 'long', month: 'short', day: 'numeric', timeZone: 'UTC' };
      return d.toLocaleDateString('en-US', opts);
    }

    /* Shopper wording. eta() = product-page line (full sentence); etaShort() = card line (no trailing period).
         before cutoff on a carrier day : "Order by 12:30 PM PT to ship today"
         store closed today             : "Store closed today. Ships Monday."
         pack-only day (Sat/Sun, carrier holiday), store still open:
                                          "Order today, we'll pack it today. Ships Monday on the next carrier pickup."
         carrier day after cutoff, store still open: "Packed today, ships tomorrow."
         after closing time             : "Ships tomorrow." (packed and shipped the same day) or "Packed tomorrow, ships Monday." */
    function wording(at, short) {
      var t = now(at);
      if (t.shipsToday) return 'Order by ' + cutoffLabel() + ' to ship today';
      var ship = shipFrom(t), sw = dayWord(ship, t.today), pw = dayWord(t.packDate, t.today);
      if (t.storeClosed) return short ? 'Store closed today, ships ' + sw : 'Store closed today. Ships ' + sw + '.';
      if (ymd(t.packDate) === ymd(t.today)) {
        if (short) return 'Packed today, ships ' + sw;
        if (isShipDay(t.today)) return 'Packed today, ships ' + sw + '.';
        return 'Order today, we’ll pack it today. Ships ' + sw + ' on the next carrier pickup.';
      }
      if (ymd(t.packDate) === ymd(ship)) return 'Ships ' + sw + (short ? '' : '.');
      return 'Packed ' + pw + ', ships ' + sw + (short ? '' : '.');
    }
    function eta(at) { return wording(at, false); }
    function etaShort(at) { return wording(at, true); }

    /* In-store pickup readiness: readyText ("within 2 hours") while the store is open and, when the text names a number of
       hours, only if that many hours remain before closing; otherwise "when we open at 10 AM" / "when we open tomorrow at
       10 AM" / "when we open Monday at 10 AM". Store-closed dates are never pickup days. */
    function pickupStatus(at, readyText) {
      var t = now(at);
      var m = String(readyText || '').match(/(\d+(?:\.\d+)?)\s*(hour|hr|minute|min)/i);
      var need = m ? Math.round(parseFloat(m[1]) * (/^h/i.test(m[2]) ? 60 : 1)) : 0;
      if (t.hours && t.mins >= t.hours.open && t.mins + need <= t.hours.close && readyText) return 'ready ' + readyText;
      if (t.hours && t.mins < t.hours.open) return 'ready when we open at ' + timeLabel(t.hours.open);
      var d = nextOpenDay(t.today), h = hoursFor(d);
      return 'ready when we open ' + dayWord(d, t.today) + ' at ' + timeLabel(h ? h.open : 600);
    }

    /* ZIP estimate. groundDays = carrier ground transit (business days) for the ZIP; opts.backorderDays (calendar days)
       pushes the handover date back for continue-selling variants with no stock. Returns
       { ship: Date, services: [{ name, days, date, tag }] }: exactly one service tagged 'Fastest' (earliest date; a tie
       goes to the faster service tier) and one 'Slowest' (latest date; a tie goes to the slower tier), and no tags at
       all when every service arrives the same day. */
    var SERVICES = [['Ground shipping', 0], ['3-Day Air', 3], ['2-Day Air', 2], ['Next Day Air', 1]]; // slowest tier first
    function estimate(groundDays, opts) {
      opts = opts || {};
      var ship = shipDate(opts.at);
      if (opts.backorderDays) ship = rollToShipDay(addDays(ship, opts.backorderDays));
      var services = SERVICES.map(function (s, tier) {
        var days = s[1] ? Math.min(s[1], groundDays) : groundDays;
        return { name: s[0], days: days, date: addBusinessDays(ship, days), tier: tier, tag: '' };
      });
      var first = services[0].date.getTime();
      var differ = services.some(function (s) { return s.date.getTime() !== first; });
      if (differ) {
        var fast = services[0], slow = services[0];
        services.forEach(function (s) {
          var t = s.date.getTime();
          if (t < fast.date.getTime() || (t === fast.date.getTime() && s.tier > fast.tier)) fast = s;
          if (t > slow.date.getTime() || (t === slow.date.getTime() && s.tier < slow.tier)) slow = s;
        });
        fast.tag = 'Fastest';
        slow.tag = 'Slowest';
      }
      return { ship: ship, services: services };
    }

    return { now: now, shipDate: shipDate, packDate: packDate, minutesToCutoff: minutesToCutoff, eta: eta, etaShort: etaShort,
      pickupStatus: pickupStatus, estimate: estimate, isShipDay: isShipDay, isOpenDay: isOpenDay,
      addBusinessDays: addBusinessDays, ymd: ymd, cutoffLabel: cutoffLabel, shipDays: shipDays, packDays: packDays };
  }

  /* ZIP -> ground transit. Digit at index N = carrier ground transit business days for 3-digit ZIP prefix N
     (0 = no data). Unchanged from live Xtra. Returns 0 for unknown prefixes or malformed input. */
  var ZIPS = '0000007707555555555555555555555555555555555555555555555055555555555555444444444444444444440000000000' +
    '4404455555444444444454444544444444444444444444444444444444444444444444444444444444444444444404444555' +
    '5555555555555055555555555555555555555555555555555555555555555555555550555555555555555555555555555555' +
    '4444444444505555555555555555555555555555055050550555505555555555555555444440444444444455555555555550' +
    '4444444444444444444044444444004444444444444444444444444444403333333333444344443344444444443334444444' +
    '4444444440444444400044444444404440440444044444444444044044444444444400444444430033433333302233222222' +
    '3333333333333333333340434333444404444444440044444044444444404440444444444444444433033333333535000000' +
    '5505555550555550555555555555554400444444440444444444404444444444455445400444444455544444555444444455' +
    '3333333333333333300033333333333332312210220222220033330333033003330000440444044444444000003302220220' +
    '3033333330333333333333333333303333222202220222222222222222222200000330111112222211111110221211144444';
  function zipGroundDays(zip) {
    var p = parseInt(String(zip || '').substring(0, 3), 10);
    return isNaN(p) ? 0 : (+ZIPS.charAt(p) || 0);
  }
  create.zipGroundDays = zipGroundDays;
  create.normalizeShipDays = normalizeShipDays;
  create.normalizeDays = normalizeDays;

  root.elShipMath = create;
  root.elZipGroundDays = zipGroundDays;
  if (typeof module === 'object' && module.exports) module.exports = create;

  var cfg = root.elShipping;
  if (!cfg || typeof document === 'undefined' || root.__elShippingDom) return;
  root.__elShippingDom = true;
  var math = create(cfg);

  function apply(rootEl) {
    var els = (rootEl || document).querySelectorAll('[data-el-ship-card-eta]');
    if (!els.length) return;
    // Product-card line shows the shipping estimate only. Pickup remains a separate checkout option.
    // EL 2026-10-12 (annotated 2026-10-15; change was published without notes, editor not recorded): was "Pick up in store, or ships ..." / "Pick up in store · Packed today, ships ...".
    // Selector renamed from [data-el-ship-eta]. Product cards no longer render this line at all (card-product.liquid),
    // so this runs with nothing to update until the card line is restored.
    var text = math.etaShort();
    for (var i = 0; i < els.length; i++) els[i].textContent = text;
  }

  root.elShippingEta = function () { return math.eta(); };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', function () { apply(); });
  else apply();
  // Re-apply when the theme swaps in new markup (filters, pagination, variant change, quick shop)
  new MutationObserver(function (muts) {
    for (var i = 0; i < muts.length; i++) {
      for (var j = 0; j < muts[i].addedNodes.length; j++) {
        var n = muts[i].addedNodes[j];
        if (n.nodeType === 1 && (n.matches('[data-el-ship-card-eta]') || n.querySelector('[data-el-ship-card-eta]'))) { apply(n.parentNode || n); }
      }
    }
  }).observe(document.documentElement, { childList: true, subtree: true });
})(typeof window !== 'undefined' ? window : globalThis);
